import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  Modal,
  message,
  Table,
  Card,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  getProduct,
  deleteProduct,
  getProductStock,
  type Product,
  type StockInfo,
} from '@/api/productApi';
import {
  getInventoryTransactions,
  deleteInventoryTransaction,
  type InventoryTransaction,
} from '@/api/productApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  inactive: 'default',
  discontinued: 'red',
};

const TYPE_COLORS: Record<string, string> = {
  in: 'green',
  out: 'red',
  adjustment: 'orange',
};

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canWrite = hasPermission('products', 'write');
  const canDelete = hasPermission('products', 'delete');

  const [product, setProduct] = useState<Product | null>(null);
  const [stockInfo, setStockInfo] = useState<StockInfo | null>(null);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [txnLoading, setTxnLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTransactions = useCallback(
    (productId: string) => {
      setTxnLoading(true);
      getInventoryTransactions({ product_id: productId, size: 200 })
        .then((res) => setTransactions(res.items))
        .catch(() => setTransactions([]))
        .finally(() => setTxnLoading(false));
    },
    []
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    Promise.all([
      getProduct(id),
      getProductStock(id).catch(() => null),
    ])
      .then(([prod, stock]) => {
        setProduct(prod);
        setStockInfo(stock);
        loadTransactions(id);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : t('app.not_found'))
      )
      .finally(() => setLoading(false));
  }, [id, t, loadTransactions]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('products.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteProduct(id);
          message.success(t('products.delete_success'));
          navigate('/products');
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  }, [id, t, navigate]);

  const handleDeleteTxn = useCallback(
    (txnId: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('inventory.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteInventoryTransaction(txnId);
            message.success(t('products.delete_success'));
            if (id) {
              loadTransactions(id);
              getProductStock(id)
                .then((s) => setStockInfo(s))
                .catch(() => null);
              getProduct(id)
                .then((p) => setProduct(p))
                .catch(() => null);
            }
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t, id, loadTransactions]
  );

  const txnColumns: ColumnsType<InventoryTransaction> = useMemo(
    () => [
      {
        title: t('inventory.field.type'),
        dataIndex: 'type',
        key: 'type',
        width: 120,
        render: (type: string) => (
          <Tag color={TYPE_COLORS[type] ?? 'default'}>
            {t(`inventory.type.${type}`, type)}
          </Tag>
        ),
      },
      {
        title: t('inventory.field.quantity'),
        dataIndex: 'quantity',
        key: 'quantity',
        width: 100,
        align: 'right',
      },
      {
        title: t('inventory.field.reference_type'),
        dataIndex: 'reference_type',
        key: 'reference_type',
        width: 120,
        render: (val: string) => t(`inventory.reference_type.${val}`, val),
      },
      {
        title: t('inventory.field.reference_id'),
        dataIndex: 'reference_id',
        key: 'reference_id',
        width: 140,
        ellipsis: true,
      },
      {
        title: t('inventory.field.warehouse_id'),
        dataIndex: 'warehouse_id',
        key: 'warehouse_id',
        width: 120,
        ellipsis: true,
      },
      {
        title: t('inventory.field.notes'),
        dataIndex: 'notes',
        key: 'notes',
        ellipsis: true,
      },
      {
        title: t('products.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 80,
        render: (_: unknown, record: InventoryTransaction) =>
          canDelete ? (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteTxn(record.id)}
            />
          ) : null,
      },
    ],
    [t, canDelete, handleDeleteTxn]
  );

  if (loading)
    return (
      <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
    );

  if (error || !product) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/products')}
        >
          {t('app.back')}
        </Button>
        <Alert
          type="error"
          message={error || t('app.not_found')}
          style={{ marginTop: 16 }}
        />
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/products')}
        >
          {t('app.back')}
        </Button>
        {canWrite && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/products/${id}/edit`)}
          >
            {t('app.edit')}
          </Button>
        )}
        {canDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
      </Space>

      <h2>{product.name}</h2>

      <Descriptions bordered column={{ xs: 1, sm: 2 }} style={{ marginBottom: 24 }}>
        <Descriptions.Item label={t('products.field.name')}>
          {product.name}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.sku')}>
          {product.sku}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.type')}>
          <Tag color={product.type === 'product' ? 'blue' : 'purple'}>
            {t(`products.type.${product.type}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.status')}>
          <Tag color={STATUS_COLORS[product.status] ?? 'default'}>
            {t(`products.status.${product.status}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.category')}>
          {product.category || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.supplier_id')}>
          {product.supplier_id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.description')} span={2}>
          {product.description || '-'}
        </Descriptions.Item>

        {/* Pricing */}
        <Descriptions.Item label={t('products.field.unit_price')}>
          {product.currency} {product.unit_price.toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.cost_price')}>
          {product.currency} {product.cost_price.toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.tax_rate')}>
          {(product.tax_rate * 100).toFixed(1)}%
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.currency')}>
          {product.currency}
        </Descriptions.Item>

        {/* Stock */}
        <Descriptions.Item label={t('products.field.stock_quantity')}>
          <Space>
            <span>{product.stock_quantity}</span>
            {stockInfo?.needs_reorder && (
              <Tag color="warning" icon={<WarningOutlined />}>
                {t('products.stock.low_stock')}
              </Tag>
            )}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.reorder_level')}>
          {product.reorder_level}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.unit_of_measure')}>
          {product.unit_of_measure || '-'}
        </Descriptions.Item>

        {/* Physical */}
        <Descriptions.Item label={t('products.field.weight')}>
          {product.weight || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.dimensions')}>
          {product.dimensions || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.tags')} span={2}>
          {product.tags?.length > 0
            ? product.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '-'}
        </Descriptions.Item>

        {/* Specifications */}
        {product.specifications &&
          Object.keys(product.specifications).length > 0 && (
            <Descriptions.Item label={t('products.field.specifications')} span={2}>
              {Object.entries(product.specifications).map(([k, v]) => (
                <Tag key={k}>
                  {k}: {v}
                </Tag>
              ))}
            </Descriptions.Item>
          )}

        {/* Audit */}
        <Descriptions.Item label={t('products.field.created_by')}>
          {product.created_by || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.created_at')}>
          {product.created_at ? new Date(product.created_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.updated_by')}>
          {product.updated_by || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('products.field.updated_at')}>
          {product.updated_at ? new Date(product.updated_at).toLocaleString() : '-'}
        </Descriptions.Item>
      </Descriptions>

      {/* Inventory Transaction History */}
      <div style={{ marginTop: 32 }}>
        <Space style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{t('inventory.title')}</h3>
          {canWrite && (
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/inventory/new?product_id=${id}`)}
            >
              {t('inventory.new_transaction')}
            </Button>
          )}
        </Space>
        <Table<InventoryTransaction>
          columns={txnColumns}
          dataSource={transactions}
          rowKey="id"
          loading={txnLoading}
          pagination={false}
          size="small"
        />
      </div>
    </div>
  );
}
